// DO NOT REMOVE: Since the d-alert component is part of the base bundle, this makes sure the web component is registered in both the iframe and the parent
import { assertNonNullable } from '@disy/lib/custom-error';
import { on } from '@disy/lib/event-util';
import { getCadenzaTopWindow } from '@disy/lib/iframe';

import 'ui/dialog/alert/alert-dialog';
import type { AlertDialog, AlertDialogOptions } from 'ui/dialog/alert/alert-dialog';

type AlertOptions = Pick<
  AlertDialogOptions,
  'submit' | 'alternativeSubmit' | 'type' | 'size' | 'cancel' | 'styleClass'
>;

type ConfirmOptions = AlertOptions &
  Pick<
    AlertDialogOptions,
    | 'submitCallback'
    | 'alternativeSubmitCallback'
    | 'loadingText'
    | 'alternativeLoadingText'
    | 'styleClass'
    | 'shouldCloseOnLoadEnd'
  > & {
    /** Callback to get the confirmDialog instance to access it later (for changing loading text etc) */
    confirmDialogCallback?: (alertDialog: AlertDialog) => void;
  };

/**
 * @return Promise to be passed to modal's setLoading method
 */
export type SubmitCallback = (alert: AlertDialog) => Promise<unknown>;

/**
 * Shows an alert modal dialog.
 *
 * @param title - Dialog title.
 * @param message - Dialog content message.
 * @param options - Additional options.
 * @param [options.submit] - Text for the submit button.
 * @param [options.alternativeSubmit] - Text for the alternative submit button.
 * @param [options.type='info'] - Alert dialog type.
 * @param [options.cancel] - Whether cancel is allowed. If true, also a cancel button is added.
 * @param [options.shouldCloseOnLoadEnd] - If false the dialog alert won't close after submit for error handling. You have to close it with alert.close(true).
 * @return A promise resolved, when the user has confirmed or rejected the alert.
 */
export function alert(title: string, message: string | Node = '', options: AlertOptions = {}) {
  return _alert(title, { type: 'info', message, ...options });
}

export function errorAlert(title: string, message: string | Node = '', options: Omit<AlertOptions, 'type'> = {}) {
  return alert(title, message, { ...options, type: 'error' }); // NOSONAR because not a native alert
}

export function confirm(title: string | Node, options?: ConfirmOptions): Promise<boolean | undefined>;
/**
 * Shows a confirmation modal dialog.
 *
 * @param title - Dialog title
 * @param message - Dialog content message
 * @param [options] - Additional options
 * @return A promise resolved, when the user has confirmed or rejected the alert
 */
export function confirm(
  title: string | Node,
  message: string | Node,
  options: ConfirmOptions,
): Promise<boolean | undefined>;
export function confirm(
  title: string | Node,
  messageOrOptions?: string | Node | ConfirmOptions,
  options: ConfirmOptions = {},
): Promise<boolean | undefined> {
  let message;
  if (typeof messageOrOptions === 'string' || messageOrOptions instanceof Node) {
    message = messageOrOptions;
  } else {
    options = messageOrOptions ?? {};
  }
  const {
    submit,
    alternativeSubmit,
    type = 'info',
    size,
    cancel = true,
    submitCallback,
    alternativeSubmitCallback,
    loadingText,
    alternativeLoadingText,
    confirmDialogCallback = () => {
      /* do nothing */
    },
    styleClass,
    shouldCloseOnLoadEnd,
  } = options;
  return _alert(title, {
    message,
    submit,
    alternativeSubmit,
    type,
    size,
    cancel,
    submitCallback,
    alternativeSubmitCallback,
    loadingText,
    alternativeLoadingText,
    confirmDialogCallback,
    styleClass,
    shouldCloseOnLoadEnd,
  });
}

function _alert(
  title: string | Node,
  options: ConfirmOptions & Pick<AlertDialogOptions, 'message'>,
): Promise<boolean | undefined> {
  const {
    confirmDialogCallback = () => {
      /* Default: do nothing */
    },
  } = options;

  const cadenzaTopWindow = getCadenzaTopWindow();
  // Get the AlertDialog web component constructor from the parent window (cadenzaTopWindow) instead of from the iframe window.
  // This is important because the element will be appended to 'cadenzaTopWindow.document.body', i.e. the parent window
  // and if we create the element inside of the iframe and append it in the parent.window, the prototype chain wouldn't be correctly inherited in Firefox
  const AlertDialogLocal = cadenzaTopWindow.customElements.get('alert-dialog');
  assertNonNullable(AlertDialogLocal);

  return new Promise((resolve) => {
    const dialog = new AlertDialogLocal(title, options) as AlertDialog;
    // Cancel resolved with undefined because false is reserved for alternative submit.
    // The logic is: submit - true, alternativeSubmit - false, cancel - undefined.
    on(dialog, 'cancel', () => resolve(undefined));
    on(dialog, 'closing', () => resolve(dialog.returnValue));
    confirmDialogCallback(dialog);
    dialog.connectAndShow(cadenzaTopWindow.document.body);
  });
}
